/**
 * FloatingReactions — Renders floating emoji animations.
 * Emojis rise from bottom-right and fade out.
 */
import { useState, useEffect, useRef } from 'react'

interface FloatingEmoji {
  id: number
  emoji: string
  x: number // random horizontal offset (%)
}

interface FloatingReactionsProps {
  /** Array of reaction events to render. Each new entry triggers a new floating emoji. */
  reactions: Array<{ emoji: string; timestamp: string }>
}

let nextId = 0

export function FloatingReactions({ reactions }: FloatingReactionsProps) {
  const [floats, setFloats] = useState<FloatingEmoji[]>([])
  const lastCountRef = useRef(0)

  // When new reactions arrive, spawn floating emojis
  useEffect(() => {
    if (reactions.length > lastCountRef.current) {
      const newOnes = reactions.slice(0, reactions.length - lastCountRef.current)
      const spawned = newOnes.map((r) => ({
        id: nextId++,
        emoji: r.emoji,
        x: 60 + Math.random() * 30, // 60-90% from left
      }))
      setFloats((prev) => [...prev, ...spawned])
      lastCountRef.current = reactions.length

      // Remove after animation (3s)
      setTimeout(() => {
        setFloats((prev) => prev.filter((f) => !spawned.some((s) => s.id === f.id)))
      }, 3000)
    }
  }, [reactions])

  if (floats.length === 0) return null

  return (
    <div className="absolute inset-0 pointer-events-none overflow-hidden z-25">
      {floats.map((f) => (
        <span
          key={f.id}
          className="absolute text-2xl animate-float-up"
          style={{ left: `${f.x}%`, bottom: '10%' }}
        >
          {f.emoji}
        </span>
      ))}
    </div>
  )
}
